import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, MapPin, Users, FileCheck, Award, AlertTriangle, Package } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

export default function FranchiseDashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  // local form state
  const [tLabel, setTLabel] = useState("");
  const [tZones, setTZones] = useState("");
  const [docKind, setDocKind] = useState("license");
  const [docLabel, setDocLabel] = useState("");
  const [docUrl, setDocUrl] = useState("");

  const reload = async () => {
    try { const { data } = await api.get("/franchise/me"); setData(data); }
    catch (e) { setErr(apiErrorMessage(e)); }
  };
  useEffect(() => { reload(); }, []);

  const addTerritory = async () => {
    if (!tLabel) return;
    setBusy(true); setErr("");
    try {
      const zones = tZones.split(",").map((s) => s.trim()).filter(Boolean);
      await api.post("/franchise/me/territories", { label: tLabel, zone_keys: zones });
      setTLabel(""); setTZones(""); await reload();
    } catch (e) { setErr(apiErrorMessage(e)); } finally { setBusy(false); }
  };

  const deleteTerritory = async (id) => {
    if (!confirm("Remove this territory?")) return;
    setBusy(true); setErr("");
    try { await api.delete(`/franchise/me/territories/${id}`); await reload(); }
    catch (e) { setErr(apiErrorMessage(e)); } finally { setBusy(false); }
  };

  const uploadDoc = async () => {
    if (!docLabel || !docUrl) return;
    setBusy(true); setErr("");
    try {
      await api.post("/franchise/me/documents", { kind: docKind, label: docLabel, url: docUrl });
      setDocLabel(""); setDocUrl(""); await reload();
    } catch (e) { setErr(apiErrorMessage(e)); } finally { setBusy(false); }
  };

  const detachPartner = async (partnerId) => {
    if (!confirm("Detach this partner from your franchise?")) return;
    setBusy(true); setErr("");
    try { await api.delete(`/franchise/me/partners/${partnerId}`); await reload(); }
    catch (e) { setErr(apiErrorMessage(e)); } finally { setBusy(false); }
  };

  if (err && !data) return <div className="text-rose-300">{err}</div>;
  if (!data) return <div className="font-bebas tracking-[0.3em] text-lifted-dim">LOADING FRANCHISE DECK…</div>;

  const compliance = data.profile.compliance.status;
  const isApproved = compliance === "approved";
  // Build delivery dispatch link with this franchise's territory zone filter
  const allZones = Array.from(new Set((data.territories || []).flatMap((t) => t.zone_keys || [])));
  const dispatchLink = allZones.length
    ? `/admin/delivery/dispatch?zones=${encodeURIComponent(allZones.join(","))}`
    : `/admin/delivery/dispatch`;

  return (
    <div data-testid="franchise-dashboard" className="space-y-10">
      <header className="fade-up">
        <div className="eyebrow flex items-center gap-2"><Building2 className="h-3 w-3" /> Franchise console</div>
        <h1 className="headline-xl">{data.profile.territory_name}</h1>
        <div className="mt-3 flex flex-wrap gap-2 items-center">
          <span className={`chip ${isApproved ? "chip--green" : ""}`}>Compliance: {compliance}</span>
          {data.franchise_certified && <span className="chip chip--purple"><Award className="h-3 w-3" /> Franchise Certified</span>}
          <span className="chip">Partners: {data.partners.length}</span>
          <span className="chip">Territories: {data.territories.length}</span>
        </div>

        {/* Section 5 — Territory revenue rollup */}
        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="franchise-revenue-rollup">
          <RollupTile
            label="Revenue · last 30d"
            value={`$${(((data.revenue?.last_30d_cents) || 0) / 100).toFixed(2)}`}
            testid="franchise-revenue-30d"
          />
          <RollupTile
            label="Orders · last 30d"
            value={String((data.revenue?.orders_last_30d) || 0)}
            testid="franchise-orders-30d"
          />
          <RollupTile
            label="Revenue · all time"
            value={`$${(((data.revenue?.all_time_cents) || 0) / 100).toFixed(2)}`}
            testid="franchise-revenue-alltime"
          />
          <RollupTile
            label="Orders · all time"
            value={String((data.revenue?.orders_all_time) || 0)}
            testid="franchise-orders-alltime"
          />
        </div>
        {!isApproved && (
          <div className="mt-4 glass-panel p-4 border-rose-500/30 flex items-start gap-3" data-testid="franchise-compliance-warning">
            <AlertTriangle className="h-5 w-5 text-rose-300" />
            <div className="text-sm">
              <div className="text-lifted-text">Your franchise is <strong>{compliance}</strong>.</div>
              <div className="text-lifted-dim">Upload franchise agreement, license, and insurance documents and ask an admin to approve before attaching new partners.</div>
            </div>
          </div>
        )}
        {err && <div className="mt-3 text-rose-300 text-sm" data-testid="franchise-form-error">{err}</div>}
      </header>

      {/* Territories */}
      <section className="glass-panel p-6" data-testid="franchise-territories-section">
        <div className="eyebrow !text-lifted-text-dim flex items-center gap-2"><MapPin className="h-3 w-3" /> Territories</div>
        <ul className="mt-4 space-y-2 text-sm">
          {data.territories.map((t) => (
            <li key={t.id} data-testid={`franchise-territory-${t.id}`} className="flex items-center justify-between border-b border-white/5 pb-2 last:border-b-0">
              <div>
                <div className="text-lifted-text">{t.label} <span className="text-lifted-mute text-xs">[{t.key}]</span></div>
                <div className="text-xs text-lifted-dim">Zones: {(t.zone_keys || []).join(", ") || "—"}</div>
              </div>
              {t.key !== "primary" && (
                <button onClick={() => deleteTerritory(t.id)} disabled={busy}
                        data-testid={`franchise-territory-delete-${t.id}`}
                        className="btn-ghost !py-1.5 !px-3 !text-[0.65rem] hover:!text-rose-300">Remove</button>
              )}
            </li>
          ))}
        </ul>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
          <input data-testid="franchise-territory-label" value={tLabel} onChange={(e) => setTLabel(e.target.value)} placeholder="Territory label" className="input-lifted text-sm sm:col-span-1" />
          <input data-testid="franchise-territory-zones" value={tZones} onChange={(e) => setTZones(e.target.value)} placeholder="zone_key, zone_key…" className="input-lifted text-sm sm:col-span-1" />
          <button data-testid="franchise-territory-add" onClick={addTerritory} disabled={busy || !tLabel} className="btn-lifted">Add territory</button>
        </div>
      </section>

      {/* Partners */}
      <section className="glass-panel p-6" data-testid="franchise-partners-section">
        <div className="flex items-center justify-between">
          <div className="eyebrow !text-lifted-text-dim flex items-center gap-2"><Users className="h-3 w-3" /> Attached partners</div>
          <span className="text-xs text-lifted-mute">Attach partners from /admin/users → grant the franchisee scope, then approve.</span>
        </div>
        <table className="w-full mt-4 text-sm">
          <thead>
            <tr className="text-left text-[0.7rem] tracking-[0.22em] uppercase text-lifted-mute font-bebas border-b border-white/5">
              <th className="py-2">Partner</th><th>Compliance</th><th>Completed</th><th>Revenue</th><th className="text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {data.partners.map((p) => (
              <tr key={p.id} data-testid={`franchise-partner-${p.id}`} className="border-b border-white/5">
                <td className="py-2"><div className="text-lifted-text">{p.business_name}</div><div className="text-xs text-lifted-mute">{p.slug}</div></td>
                <td>{{
                  pending: <span className="chip">Pending</span>,
                  approved: <span className="chip chip--green">Approved</span>,
                  suspended: <span className="chip">Suspended</span>,
                  rejected: <span className="chip">Rejected</span>,
                }[p.compliance.status]}</td>
                <td className="font-anton text-lifted-green">{p.stats.orders_completed}</td>
                <td className="text-lifted-text">${((p.stats.total_revenue_cents || 0) / 100).toFixed(2)}</td>
                <td className="text-right">
                  <button onClick={() => detachPartner(p.id)} disabled={busy} data-testid={`franchise-partner-detach-${p.id}`}
                          className="btn-ghost !py-1.5 !px-3 !text-[0.65rem] hover:!text-rose-300">Detach</button>
                </td>
              </tr>
            ))}
            {data.partners.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-lifted-mute">No partners attached yet.</td></tr>}
          </tbody>
        </table>
      </section>

      {/* Orders in territory (READ-ONLY rollup of delivery_orders) */}
      <section className="glass-panel p-6" data-testid="franchise-orders-section">
        <div className="flex items-center justify-between">
          <div className="eyebrow !text-lifted-text-dim flex items-center gap-2"><Package className="h-3 w-3" /> Recent orders in territory</div>
          <Link to={dispatchLink} className="btn-ghost !py-1.5 !px-3 !text-[0.65rem]" data-testid="franchise-open-dispatch-board">→ Open dispatch board</Link>
        </div>
        <table className="w-full mt-4 text-sm">
          <thead>
            <tr className="text-left text-[0.7rem] tracking-[0.22em] uppercase text-lifted-mute font-bebas border-b border-white/5">
              <th className="py-2">When</th><th>Source</th><th>Drop-off</th><th>Zone</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {data.orders.map((o) => (
              <tr key={o.id} data-testid={`franchise-order-${o.id}`} className="border-b border-white/5">
                <td className="py-2 text-xs text-lifted-dim">{o.created_at?.slice(0,16).replace("T"," ")}</td>
                <td className="text-xs"><span className="chip">{o.source}</span></td>
                <td className="text-xs text-lifted-dim">{o.dropoff_address}</td>
                <td className="text-xs text-lifted-text">{o.zone_key || "—"}</td>
                <td>{{
                  created: <span className="chip">Created</span>,
                  assigned: <span className="chip chip--purple">Assigned</span>,
                  picked_up: <span className="chip chip--purple">Picked up</span>,
                  in_transit: <span className="chip chip--purple">In transit</span>,
                  delivered: <span className="chip chip--green">Delivered</span>,
                  failed: <span className="chip">Failed</span>,
                  cancelled: <span className="chip">Cancelled</span>,
                }[o.status]}</td>
              </tr>
            ))}
            {data.orders.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-lifted-mute">No orders in your territory zones yet.</td></tr>}
          </tbody>
        </table>
      </section>

      {/* Documents */}
      <section className="glass-panel p-6" data-testid="franchise-documents-section">
        <div className="eyebrow !text-lifted-text-dim flex items-center gap-2"><FileCheck className="h-3 w-3" /> Compliance documents</div>
        <ul className="mt-3 text-sm space-y-2">
          {data.documents.map((d) => (
            <li key={d.id} data-testid={`franchise-doc-${d.id}`} className="flex items-center justify-between border-b border-white/5 pb-2 last:border-b-0">
              <div>
                <div className="text-lifted-text">{d.label} · <span className="text-lifted-mute">{d.kind}</span></div>
                <div className="text-xs text-lifted-dim">{d.url}</div>
              </div>
              {{ pending: <span className="chip">Pending</span>,
                 approved: <span className="chip chip--green">Approved</span>,
                 rejected: <span className="chip">Rejected</span> }[d.status]}
            </li>
          ))}
          {data.documents.length === 0 && <li className="text-lifted-mute text-xs">No documents uploaded yet.</li>}
        </ul>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-4 gap-2">
          <select data-testid="franchise-doc-kind" value={docKind} onChange={(e) => setDocKind(e.target.value)} className="input-lifted text-sm">
            <option value="license">License</option>
            <option value="insurance">Insurance</option>
            <option value="franchise_agreement">Franchise agreement</option>
            <option value="other">Other</option>
          </select>
          <input data-testid="franchise-doc-label" value={docLabel} onChange={(e) => setDocLabel(e.target.value)} placeholder="Document label" className="input-lifted text-sm" />
          <input data-testid="franchise-doc-url" value={docUrl} onChange={(e) => setDocUrl(e.target.value)} placeholder="https://…" className="input-lifted text-sm" />
          <button data-testid="franchise-doc-upload" onClick={uploadDoc} disabled={busy || !docLabel || !docUrl} className="btn-lifted">Upload</button>
        </div>
      </section>

      <Link to="/training/franchise" className="btn-ghost inline-flex" data-testid="franchise-certification-link">→ Franchise Operator certification</Link>
    </div>
  );
}

function RollupTile({ label, value, testid }) {
  return (
    <div className="glass-panel p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bebas text-lifted-dim">
        {label}
      </div>
      <div className="font-anton text-2xl text-white mt-1">{value}</div>
    </div>
  );
}
